import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { getRequest } from "../../../utils/api";

interface EditBusinessModalProps {
  show: boolean;
  onHide: () => void;
  form: any;
  handleInputChange: (e: React.ChangeEvent<any>) => void;
  onSave: () => void;
  isSaving: boolean;
}

const EditBusinessModal = ({
  show,
  onHide,
  form,
  handleInputChange,
  onSave,
  isSaving,
}: EditBusinessModalProps) => {
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);

  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);

  const fetchCountries = async () => {
    setLoadingCountries(true);

    try {
      const response = await getRequest("/countries-fetch");

      if (response.status === "success") {
        setCountries(response.data || []);
      } else {
        setCountries([]);
      }
    } catch (err) {
      console.log(err);
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  };

  const fetchStates = async (countryId: string) => {
    if (!countryId) {
      setStates([]);
      return;
    }

    setLoadingStates(true);

    try {
      const response = await getRequest(`/states-fetch/${countryId}`);

      if (response.status === "success") {
        setStates(response.data || []);
      } else {
        setStates([]);
      }
    } catch (err) {
      console.log(err);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchCountries();
    }
  }, [show]);

  useEffect(() => {
    if (show && form.user_country) {
      fetchStates(form.user_country);
    }
  }, [show, form.user_country]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleInputChange(e);
    fetchStates(e.target.value);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">Edit Business Profile</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Company Name</Form.Label>
                <Form.Control
                  type="text"
                  name="user_companyName"
                  value={form.user_companyName || ""}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Website</Form.Label>
                <Form.Control
                  type="text"
                  name="user_website"
                  value={form.user_website || ""}
                  placeholder="https://"
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Country</Form.Label>

                <Form.Select
                  name="user_country"
                  value={form.user_country || ""}
                  onChange={handleCountryChange}
                  disabled={loadingCountries}
                >
                  <option value="">
                    {loadingCountries
                      ? "Loading Countries..."
                      : "Select Country"}
                  </option>

                  {countries.map((country) => (
                    <option key={country.country_id} value={country.country_id}>
                      {country.country_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>State</Form.Label>

                <Form.Select
                  name="user_state"
                  value={form.user_state || ""}
                  onChange={handleInputChange}
                  disabled={loadingStates || !form.user_country}
                >
                  <option value="">
                    {loadingStates ? "Loading States..." : "Select State"}
                  </option>

                  {states.map((state) => (
                    <option key={state.state_id} value={state.state_id}>
                      {state.state_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Industry</Form.Label>

                <Form.Select
                  name="user_industry"
                  value={form.user_industry || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select Industry</option>
                  <option value="Ecommerce">Ecommerce</option>
                  <option value="Education">Education</option>
                  <option value="Automotive">Automotive</option>
                  <option value="IT Services">IT Services</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Finance & Banking">Finance & Banking</option>
                  <option value="Medical & Health">Medical & Health</option>
                  <option value="Resturant & Hotel">Resturant & Hotel</option>
                  <option value="Beauty, Spa and salon">
                    Beauty, Spa and salon
                  </option>
                  <option value="Shopping & Retail">Shopping & Retail</option>
                  <option value="Travel & Transportation">
                    Travel & Transportation
                  </option>
                  <option value="Professional Services">
                    Professional Services
                  </option>
                  <option value="Other">Other</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Company Size</Form.Label>

                <Form.Select
                  name="user_companySizeRange"
                  value={form.user_companySizeRange || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select Company Size</option>
                  <option value="1-20">1 - 20</option>
                  <option value="20-50">20 - 50</option>
                  <option value="50-200">50 - 200</option>
                  <option value="200+">200+</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onHide} disabled={isSaving}>
          Cancel
        </Button>

        <Button variant="primary" onClick={onSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditBusinessModal;
